import { useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface AttachmentState {
  id: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  status: 'uploading' | 'extracting' | 'ready' | 'failed';
  error?: string;
}

const ACCEPTED_MIME_TYPES: Record<string, string> = {
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.csv': 'text/csv',
  '.pdf': 'application/pdf',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

const ACCEPT_STRING = Object.keys(ACCEPTED_MIME_TYPES).join(',');
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_FILES = 5;
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15; // 30 seconds total

function sanitizeFileName(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

function getMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = '.' + file.name.split('.').pop()?.toLowerCase();
  return ACCEPTED_MIME_TYPES[ext] || 'application/octet-stream';
}

export function useBuilderAttachments() {
  const [attachments, setAttachments] = useState<AttachmentState[]>([]);
  const pollTimersRef = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  const attachmentIds = attachments
    .filter(a => a.status === 'ready')
    .map(a => a.id);

  const updateAttachment = useCallback((id: string, update: Partial<AttachmentState>) => {
    setAttachments(prev => prev.map(a => a.id === id ? { ...a, ...update } : a));
  }, []);

  const stopPolling = useCallback((attachmentId: string) => {
    const timer = pollTimersRef.current.get(attachmentId);
    if (timer) {
      clearInterval(timer);
      pollTimersRef.current.delete(attachmentId);
    }
  }, []);

  /**
   * Poll the builder_attachments row until extraction completes or fails.
   */
  const pollExtraction = useCallback((attachmentId: string) => {
    let attempts = 0;

    const timer = setInterval(async () => {
      attempts++;

      if (attempts > POLL_MAX_ATTEMPTS) {
        stopPolling(attachmentId);
        updateAttachment(attachmentId, {
          status: 'failed',
          error: 'Text extraction timed out. Try a smaller file or paste the text manually.',
        });
        return;
      }

      const { data, error } = await supabase
        .from('builder_attachments')
        .select('extraction_status, extraction_error')
        .eq('id', attachmentId)
        .single();

      if (error) {
        console.error('[useBuilderAttachments] Poll error:', error);
        return; // Keep polling, might be transient
      }

      if (data.extraction_status === 'completed') {
        stopPolling(attachmentId);
        updateAttachment(attachmentId, { status: 'ready' });
      } else if (data.extraction_status === 'failed') {
        stopPolling(attachmentId);
        updateAttachment(attachmentId, {
          status: 'failed',
          error: data.extraction_error || 'Text extraction failed. Try pasting the text manually.',
        });
      }
      // 'pending' or 'processing' — keep polling
    }, POLL_INTERVAL_MS);

    pollTimersRef.current.set(attachmentId, timer);
  }, [stopPolling, updateAttachment]);

  /**
   * Upload a file and trigger text extraction.
   */
  const uploadFile = useCallback(async (file: File, conversationId: string) => {
    // Validate file count
    if (attachments.length >= MAX_FILES) {
      throw new Error(`Maximum ${MAX_FILES} files per conversation`);
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(`File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB`);
    }

    // Validate file type
    const ext = '.' + (file.name.split('.').pop()?.toLowerCase() || '');
    if (!ACCEPTED_MIME_TYPES[ext]) {
      throw new Error(`Unsupported file type. Accepted: ${Object.keys(ACCEPTED_MIME_TYPES).join(', ')}`);
    }

    const mimeType = getMimeType(file);

    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Create a temporary ID for UI state tracking
    const tempId = `temp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    const safeName = sanitizeFileName(file.name);
    const storagePath = `${user.id}/${conversationId}/${Date.now()}_${safeName}`;

    // Add to local state as uploading
    setAttachments(prev => [...prev, {
      id: tempId,
      fileName: file.name,
      fileSize: file.size,
      mimeType,
      status: 'uploading',
    }]);

    try {
      // 1. Upload to storage
      const { error: uploadError } = await supabase.storage
        .from('builder-attachments')
        .upload(storagePath, file, { contentType: mimeType, upsert: false });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // 2. Insert metadata row
      const { data: row, error: insertError } = await supabase
        .from('builder_attachments')
        .insert({
          conversation_id: conversationId,
          user_id: user.id,
          storage_path: storagePath,
          file_name: file.name,
          file_size: file.size,
          mime_type: mimeType,
        })
        .select('id')
        .single();

      if (insertError || !row) {
        // Clean up storage on insert failure
        await supabase.storage.from('builder-attachments').remove([storagePath]);
        throw new Error(`Failed to save attachment metadata: ${insertError?.message}`);
      }

      // Replace temp ID with real DB ID, update status
      setAttachments(prev => prev.map(a =>
        a.id === tempId ? { ...a, id: row.id, status: 'extracting' } : a
      ));

      // 3. Call extract-file-text edge function (fire and forget — we poll for status)
      supabase.functions.invoke('extract-file-text', {
        body: { attachment_id: row.id },
      }).catch(err => {
        console.error('[useBuilderAttachments] Extraction invocation failed:', err);
      });

      // 4. Start polling for extraction completion
      pollExtraction(row.id);

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Upload failed';
      setAttachments(prev => prev.map(a =>
        a.id === tempId ? { ...a, status: 'failed', error: errorMsg } : a
      ));
    }
  }, [attachments.length, pollExtraction]);

  /**
   * Remove an attachment (deletes from storage and DB).
   */
  const removeAttachment = useCallback(async (id: string) => {
    stopPolling(id);

    // Get storage path before deleting
    const { data } = await supabase
      .from('builder_attachments')
      .select('storage_path')
      .eq('id', id)
      .single();

    // Delete DB row
    await supabase
      .from('builder_attachments')
      .delete()
      .eq('id', id);

    // Delete from storage
    if (data?.storage_path) {
      await supabase.storage
        .from('builder-attachments')
        .remove([data.storage_path]);
    }

    // Remove from local state (also handle temp IDs that start with temp_)
    setAttachments(prev => prev.filter(a => a.id !== id));
  }, [stopPolling]);

  /**
   * Load existing attachments for a conversation (used when resuming).
   */
  const loadAttachments = useCallback(async (conversationId: string) => {
    const { data, error } = await supabase
      .from('builder_attachments')
      .select('id, file_name, file_size, mime_type, extraction_status, extraction_error')
      .eq('conversation_id', conversationId);

    if (error || !data) {
      console.error('[useBuilderAttachments] Failed to load attachments:', error);
      return;
    }

    const loaded: AttachmentState[] = data.map(row => ({
      id: row.id,
      fileName: row.file_name,
      fileSize: row.file_size,
      mimeType: row.mime_type,
      status: row.extraction_status === 'completed' ? 'ready' as const
        : row.extraction_status === 'failed' ? 'failed' as const
        : 'extracting' as const,
      error: row.extraction_error || undefined,
    }));

    setAttachments(loaded);

    // Resume polling for any still-pending extractions
    for (const a of loaded) {
      if (a.status === 'extracting') {
        pollExtraction(a.id);
      }
    }
  }, [pollExtraction]);

  return {
    attachments,
    attachmentIds,
    uploadFile,
    removeAttachment,
    loadAttachments,
    acceptString: ACCEPT_STRING,
    maxFiles: MAX_FILES,
  };
}
